import React from 'react'
import { MIN_COLUMNS, MIN_ROWS } from '../constants/guide-limits'
import { resolveGutterBlur } from '../helpers/gutter-fit'
import type { GuideUnit } from '../types/guide-unit'

type UseLinkedGutterHandlersArgs = {
  unit: GuideUnit
  guttersLinked: boolean
  setGuttersLinked: React.Dispatch<React.SetStateAction<boolean>>
  colGutterInput: string
  rowGutterInput: string
  setColGutterInput: (value: string) => void
  setRowGutterInput: (value: string) => void
  /** Parsed counts (null while the count field is invalid). */
  columns: number | null
  rows: number | null
  pageInnerWidth: number
  pageInnerHeight: number
}

/**
 * Column/row gutter edits. While the gutters are linked, typing in either field mirrors into the
 * other, and blur normalizes both (each against its own axis).
 */
export function useLinkedGutterHandlers({
  unit,
  guttersLinked,
  setGuttersLinked,
  colGutterInput,
  rowGutterInput,
  setColGutterInput,
  setRowGutterInput,
  columns,
  rows,
  pageInnerWidth,
  pageInnerHeight,
}: UseLinkedGutterHandlersArgs) {
  const handleColumnGutterChange = React.useCallback(
    (nextValue: string) => {
      setColGutterInput(nextValue)
      if (guttersLinked) {
        setRowGutterInput(nextValue)
      }
    },
    [guttersLinked, setColGutterInput, setRowGutterInput]
  )

  const handleRowGutterChange = React.useCallback(
    (nextValue: string) => {
      setRowGutterInput(nextValue)
      if (guttersLinked) {
        setColGutterInput(nextValue)
      }
    },
    [guttersLinked, setColGutterInput, setRowGutterInput]
  )

  const handleToggleGutterLink = React.useCallback(() => {
    setGuttersLinked((previouslyLinked) => {
      const nextLinked = !previouslyLinked

      if (nextLinked) {
        setRowGutterInput(colGutterInput)
      }

      return nextLinked
    })
  }, [colGutterInput, setGuttersLinked, setRowGutterInput])

  const handleColumnGutterBlur = React.useCallback(() => {
    const next = resolveGutterBlur(
      colGutterInput,
      unit,
      guttersLinked,
      { count: columns ?? MIN_COLUMNS, pageInnerSize: pageInnerWidth },
      { count: rows ?? MIN_ROWS, pageInnerSize: pageInnerHeight }
    )

    if (next == null) return
    setColGutterInput(next.own)
    if (next.other != null) setRowGutterInput(next.other)
  }, [
    colGutterInput,
    columns,
    guttersLinked,
    pageInnerHeight,
    pageInnerWidth,
    rows,
    setColGutterInput,
    setRowGutterInput,
    unit,
  ])

  const handleRowGutterBlur = React.useCallback(() => {
    const next = resolveGutterBlur(
      rowGutterInput,
      unit,
      guttersLinked,
      { count: rows ?? MIN_ROWS, pageInnerSize: pageInnerHeight },
      { count: columns ?? MIN_COLUMNS, pageInnerSize: pageInnerWidth }
    )

    if (next == null) return
    setRowGutterInput(next.own)
    if (next.other != null) setColGutterInput(next.other)
  }, [
    columns,
    guttersLinked,
    pageInnerHeight,
    pageInnerWidth,
    rowGutterInput,
    rows,
    setColGutterInput,
    setRowGutterInput,
    unit,
  ])

  return {
    handleColumnGutterChange,
    handleRowGutterChange,
    handleToggleGutterLink,
    handleColumnGutterBlur,
    handleRowGutterBlur,
  }
}
